import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

const apiUrl = process.env.REACT_APP_API_URL;
const publicKey = process.env.REACT_APP_VAPID_PUBLIC_KEY || process.env.REACT_APP_PUSH_PUBLIC_KEY;

const base64UrlToUint8Array = (base64Url) => {
  const padding = '='.repeat((4 - (base64Url.length % 4)) % 4);
  const base64 = `${base64Url}${padding}`.replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
};

const getSupport = () => ({
  serviceWorker: 'serviceWorker' in navigator,
  pushManager: 'PushManager' in window,
  notification: 'Notification' in window,
  secureContext: window.isSecureContext,
});

const hasPushSupport = (support) => (
  support.serviceWorker
  && support.pushManager
  && support.notification
  && support.secureContext
);

export default function usePushNotifications() {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const [support, setSupport] = useState(() => getSupport());
  const [permission, setPermission] = useState(() => (
    'Notification' in window ? window.Notification.permission : 'unsupported'
  ));
  const [subscription, setSubscription] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  const supported = useMemo(() => hasPushSupport(support), [support]);
  const configured = Boolean(publicKey);
  const enabled = Boolean(subscription);

  const getToken = useCallback(() => getAccessTokenSilently({
    authorizationParams: { audience: process.env.REACT_APP_API_AUDIENCE },
  }), [getAccessTokenSilently]);

  const readExistingSubscription = useCallback(async () => {
    setSupport(getSupport());

    if (!supported) {
      return null;
    }

    const registration = await navigator.serviceWorker.ready;
    const existing = await registration.pushManager.getSubscription();
    setSubscription(existing);
    return existing;
  }, [supported]);

  useEffect(() => {
    readExistingSubscription().catch(() => {
      setSubscription(null);
    });
  }, [readExistingSubscription]);

  const saveSubscription = useCallback(async (pushSubscription) => {
    if (!apiUrl || !pushSubscription) {
      return;
    }

    const token = await getToken();
    const response = await fetch(`${apiUrl}/api/notifications/push-subscription`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(pushSubscription.toJSON()),
    });

    if (!response.ok) {
      throw new Error(`Push subscription save failed (${response.status})`);
    }
  }, [getToken]);

  const enablePush = useCallback(async () => {
    setError('');

    if (!isAuthenticated) {
      setError('Sign in to enable push alerts.');
      return false;
    }

    if (!supported) {
      setError('This browser does not support web push notifications.');
      return false;
    }

    if (!configured) {
      setError('Push alerts are not configured for this environment yet.');
      return false;
    }

    setIsSaving(true);

    try {
      const nextPermission = await window.Notification.requestPermission();
      setPermission(nextPermission);

      if (nextPermission !== 'granted') {
        setError(nextPermission === 'denied'
          ? 'Notifications are blocked in this browser.'
          : 'Notification permission was not granted.');
        return false;
      }

      const registration = await navigator.serviceWorker.ready;
      const existing = await registration.pushManager.getSubscription();
      const nextSubscription = existing || await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: base64UrlToUint8Array(publicKey),
      });

      await saveSubscription(nextSubscription);
      setSubscription(nextSubscription);
      return true;
    } catch (nextError) {
      setError(nextError.message || 'Could not enable push alerts.');
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [configured, isAuthenticated, saveSubscription, supported]);

  const disablePush = useCallback(async () => {
    setError('');

    if (!subscription) {
      return true;
    }

    setIsSaving(true);

    try {
      const token = await getToken();
      await fetch(`${apiUrl}/api/notifications/push-subscription`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ endpoint: subscription.endpoint }),
      });
      await subscription.unsubscribe();
      setSubscription(null);
      return true;
    } catch (nextError) {
      setError(nextError.message || 'Could not disable push alerts.');
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [getToken, subscription]);

  return {
    configured,
    enabled,
    error,
    isSaving,
    permission,
    supported,
    enablePush,
    disablePush,
  };
}
